import { useCallback } from 'react';
import { useTranslation } from '../../../contexts/TranslationContext';
import { durationMinutes, NO_DATA } from '../utils/metricDisplay';

const MINUTES_PER_DAY = 60 * 24;

/**
 * `daysValue` is the one metric formatter that spells its unit out as a word,
 * so it needs `t` and cannot live in `metricDisplay.ts` alongside the pure
 * formatters. It moved here rather than dragging React context into that
 * module; everything else there stays pure and directly unit-testable.
 */
export function useMetricDisplay() {
  const { t } = useTranslation();

  /**
   * Days with no measurement behind them are "—", never "0.0 days": a cycle
   * time of zero asserts that deals close the same day they arrive.
   *
   * Deliberately NOT a `count`-based plural. `toFixed` renders 1 as "1.0", and
   * the English copy reads "1.0 days"; a `_one` variant would select on
   * `count === 1` and turn that into "1.0 day", changing a string this sweep
   * exists to preserve. Spanish and Portuguese pluralise decimals the same way.
   */
  const daysValue = useCallback(
    (days: number | null | undefined, fractionDigits = 1): string => {
      if (days === null || days === undefined) return NO_DATA;
      // Under a day, "days" cannot express the measurement: a five-minute
      // transit read "0.0 days", which is the same string a stage with nothing
      // measured would deserve and none of the information the reading holds.
      // `durationMinutes` already renders m / h / h+m for response time.
      if (days < 1) return durationMinutes(days * MINUTES_PER_DAY);
      return t('analytics.units.days', { value: days.toFixed(fractionDigits) });
    },
    [t],
  );

  return { daysValue };
}
